import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sparkles, RefreshCw, TrendingUp, TrendingDown, Target,
  BarChart3, AlertTriangle, Globe, ChevronRight, Zap, Clock,
  Search, Hash, Filter, Calendar as CalendarIcon, CheckCircle2, XCircle,
  MessageSquare, ThumbsUp, Layers, Eye, Heart, ArrowUpRight, Users
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: "ko", label: "KO" },
  { code: "zh", label: "CN" },
  { code: "en", label: "EN" },
];

const PLATFORMS = [
  { value: "youtube", label: "YouTube", color: "bg-red-500/15 text-red-400 border-red-500/30" },
  { value: "tiktok", label: "TikTok", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" },
  { value: "instagram", label: "Instagram", color: "bg-pink-500/15 text-pink-400 border-pink-500/30" },
  { value: "douyin", label: "Douyin", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  { value: "xiaohongshu", label: "XHS", color: "bg-rose-500/15 text-rose-400 border-rose-500/30" },
  { value: "weibo", label: "Weibo", color: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  { value: "bilibili", label: "Bilibili", color: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
];

const PERIODS = [
  { value: "7d", label: "7D" },
  { value: "30d", label: "30D" },
  { value: "90d", label: "90D" },
];

const UI = {
  ko: {
    title: "AI 타겟 분석",
    subtitle: "GPT-5.2 기반 맞춤형 인사이트",
    tabs: { keyword: "키워드 분석", platform: "플랫폼 분석", posts: "게시물 분석" },
    keyword: { placeholder: "분석할 키워드 입력...", search: "검색", analyze: "분석 실행", found: "개 게시물 발견", noResult: "키워드와 일치하는 게시물이 없습니다." },
    platform: { select: "플랫폼 선택", period: "기간", analyze: "분석 실행", custom: "직접 입력" },
    post: { search: "게시물 검색...", selected: "개 선택됨", max: "최대 10개", analyze: "분석 실행", noResult: "게시물을 검색해주세요." },
    common: { analyzing: "AI 분석 중...", cached: "캐시", fresh: "최신", generatedAt: "생성", error: "분석 생성 중 오류가 발생했습니다.", comments: "댓글", views: "조회수", likes: "좋아요", er: "참여율" },
  },
  zh: {
    title: "AI 目标分析",
    subtitle: "基于GPT-5.2的精准洞察",
    tabs: { keyword: "关键词分析", platform: "平台分析", posts: "帖子分析" },
    keyword: { placeholder: "输入要分析的关键词...", search: "搜索", analyze: "开始分析", found: "条帖子匹配", noResult: "未找到匹配的帖子。" },
    platform: { select: "选择平台", period: "时间段", analyze: "开始分析", custom: "自定义" },
    post: { search: "搜索帖子...", selected: "已选", max: "最多10条", analyze: "开始分析", noResult: "请先搜索帖子。" },
    common: { analyzing: "AI分析中...", cached: "缓存", fresh: "最新", generatedAt: "生成于", error: "分析生成失败，请重试。", comments: "评论", views: "播放", likes: "点赞", er: "互动率" },
  },
  en: {
    title: "AI Targeted Analysis",
    subtitle: "Precision insights powered by GPT-5.2",
    tabs: { keyword: "Keyword", platform: "Platform", posts: "Posts" },
    keyword: { placeholder: "Enter keyword to analyze...", search: "Search", analyze: "Analyze", found: "posts found", noResult: "No posts match this keyword." },
    platform: { select: "Select Platform", period: "Period", analyze: "Analyze", custom: "Custom" },
    post: { search: "Search posts...", selected: "selected", max: "Max 10", analyze: "Analyze", noResult: "Search for posts first." },
    common: { analyzing: "AI analyzing...", cached: "Cached", fresh: "Fresh", generatedAt: "Generated", error: "Analysis failed. Please try again.", comments: "Comments", views: "Views", likes: "Likes", er: "ER" },
  },
};

const SECTION_STYLES = {
  summary: { icon: BarChart3, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  performance: { icon: TrendingUp, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
  platform_comparison: { icon: Globe, color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20" },
  benchmark: { icon: Target, color: "text-teal-400", bg: "bg-teal-500/10", border: "border-teal-500/20" },
  individual_analysis: { icon: Layers, color: "text-indigo-400", bg: "bg-indigo-500/10", border: "border-indigo-500/20" },
  comment_analysis: { icon: MessageSquare, color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20" },
  recommendations: { icon: Target, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
  attention_needed: { icon: AlertTriangle, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
};

const getPlatformColor = (name) => PLATFORMS.find(p => p.value === name?.toLowerCase())?.color || "bg-gray-500/15 text-gray-400 border-gray-500/30";

// ============================================================
// Section Renderers
// ============================================================
const SummaryRenderer = ({ section }) => (
  <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">{section.content}</p>
);

const PerformanceRenderer = ({ section }) => (
  <div className="space-y-3">
    {section.highlights?.length > 0 && (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {section.highlights.map((h, i) => (
          <div key={i} className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
            <div className="text-xs text-muted-foreground">{h.label}</div>
            <div className="text-lg font-bold text-foreground mt-0.5">{h.value}</div>
            {h.note && <div className="text-[10px] text-emerald-400 mt-1">{h.note}</div>}
          </div>
        ))}
      </div>
    )}
    <p className="text-sm text-foreground/85 leading-relaxed">{section.content}</p>
  </div>
);

const PlatformComparisonRenderer = ({ section }) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {section.platforms?.map((p, i) => (
      <div key={i} className={`rounded-lg border p-3 ${getPlatformColor(p.name).split(' ').pop()}`}>
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className={`text-[10px] ${getPlatformColor(p.name)}`}>{p.name}</Badge>
          <span className="text-xs text-muted-foreground">{p.posts} posts</span>
        </div>
        <p className="text-sm text-foreground/80">{p.analysis}</p>
      </div>
    ))}
  </div>
);

const BenchmarkRenderer = ({ section }) => (
  <div className="space-y-3">
    {section.content && <p className="text-sm text-foreground/85 leading-relaxed">{section.content}</p>}
    {section.vs_overall && <p className="text-sm text-foreground/80">{section.vs_overall}</p>}
    <div className="grid sm:grid-cols-2 gap-3">
      {section.strengths?.length > 0 && (
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-emerald-400 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Strengths</span>
          {section.strengths.map((s, i) => (
            <div key={i} className="text-xs text-foreground/75 pl-4 flex items-start gap-1.5">
              <ChevronRight className="h-3 w-3 mt-0.5 text-emerald-400 shrink-0" />{s}
            </div>
          ))}
        </div>
      )}
      {section.weaknesses?.length > 0 && (
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-rose-400 flex items-center gap-1"><XCircle className="h-3 w-3" /> Weaknesses</span>
          {section.weaknesses.map((w, i) => (
            <div key={i} className="text-xs text-foreground/75 pl-4 flex items-start gap-1.5">
              <ChevronRight className="h-3 w-3 mt-0.5 text-rose-400 shrink-0" />{w}
            </div>
          ))}
        </div>
      )}
    </div>
    {section.above_average?.length > 0 && (
      <div className="text-xs text-emerald-400 flex flex-wrap gap-1">
        <ArrowUpRight className="h-3 w-3" />
        {section.above_average.map((t, i) => <Badge key={i} variant="outline" className="text-[10px] bg-emerald-500/10 border-emerald-500/20 text-emerald-400">{t}</Badge>)}
      </div>
    )}
    {section.below_average?.length > 0 && (
      <div className="text-xs text-rose-400 flex flex-wrap gap-1">
        <TrendingDown className="h-3 w-3" />
        {section.below_average.map((t, i) => <Badge key={i} variant="outline" className="text-[10px] bg-rose-500/10 border-rose-500/20 text-rose-400">{t}</Badge>)}
      </div>
    )}
  </div>
);

const IndividualAnalysisRenderer = ({ section }) => (
  <div className="space-y-2">
    {section.posts?.map((p, i) => (
      <div key={i} className="p-3 rounded-lg border border-border/50 bg-card/50">
        <div className="flex items-center gap-2 mb-1.5">
          <Badge variant="outline" className={`text-[10px] ${getPlatformColor(p.platform)}`}>{p.platform}</Badge>
          <span className="text-sm font-medium text-foreground truncate">{p.title}</span>
        </div>
        <p className="text-xs text-amber-400 mb-1">{p.verdict}</p>
        <p className="text-xs text-foreground/70">{p.key_insight}</p>
      </div>
    ))}
  </div>
);

const CommentAnalysisRenderer = ({ section }) => (
  <div className="space-y-3">
    {section.sentiment && <p className="text-sm text-foreground/85">{section.sentiment}</p>}
    {section.key_themes?.length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        {section.key_themes.map((t, i) => (
          <Badge key={i} variant="outline" className="text-[10px] bg-rose-500/10 text-rose-400 border-rose-500/20">
            <Hash className="h-2.5 w-2.5 mr-0.5" />{t}
          </Badge>
        ))}
      </div>
    )}
    {section.content && <p className="text-sm text-foreground/80 leading-relaxed">{section.content}</p>}
  </div>
);

const RecommendationsRenderer = ({ section }) => (
  <div className="space-y-2">
    {section.items?.map((r, i) => (
      <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/10">
        <div className="flex items-center justify-center h-5 w-5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</div>
        <span className="text-sm text-foreground/85">{r}</span>
      </div>
    ))}
  </div>
);

const AttentionRenderer = ({ section }) => (
  <div className="space-y-2">
    {section.items?.map((item, i) => (
      <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-red-500/5 border border-red-500/10">
        <AlertTriangle className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
        <span className="text-sm text-foreground/80">{item}</span>
      </div>
    ))}
  </div>
);

const RENDERERS = {
  summary: SummaryRenderer,
  performance: PerformanceRenderer,
  platform_comparison: PlatformComparisonRenderer,
  benchmark: BenchmarkRenderer,
  individual_analysis: IndividualAnalysisRenderer,
  comment_analysis: CommentAnalysisRenderer,
  recommendations: RecommendationsRenderer,
  attention_needed: AttentionRenderer,
};

// ============================================================
// Result Display
// ============================================================
const AnalysisResult = ({ data }) => {
  if (!data || !data.sections?.length) return null;
  const isEmpty = data.sections.length === 1 && data.sections[0].type === "empty";
  if (isEmpty) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {data.sections[0].content}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {data.sections.map((section, i) => {
        const style = SECTION_STYLES[section.type] || SECTION_STYLES.summary;
        const Icon = style.icon;
        const Renderer = RENDERERS[section.type] || SummaryRenderer;
        return (
          <Card key={i} className={`border ${style.border}`} data-testid={`section-${section.type}`}>
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <div className={`p-1 rounded-md ${style.bg}`}><Icon className={`h-3.5 w-3.5 ${style.color}`} /></div>
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <Renderer section={section} />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const MetaBadges = ({ data, t }) => {
  if (!data?.generated_at) return null;
  return (
    <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
      <Clock className="h-3 w-3" />
      <span>{t.common.generatedAt}: {new Date(data.generated_at).toLocaleString()}</span>
      <Badge variant="outline" className={`text-[10px] ${data.cached ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"}`}>
        {data.cached ? t.common.cached : t.common.fresh}
      </Badge>
      {data.meta?.matched_posts != null && <span>{data.meta.matched_posts} posts</span>}
      {data.meta?.total_comments > 0 && <span>{data.meta.total_comments} {t.common.comments}</span>}
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="space-y-3">
    {[1, 2, 3].map(i => (
      <Card key={i} className="animate-pulse">
        <CardHeader className="pb-2"><div className="h-4 w-40 bg-muted rounded" /></CardHeader>
        <CardContent className="space-y-2"><div className="h-3 w-full bg-muted rounded" /><div className="h-3 w-2/3 bg-muted rounded" /></CardContent>
      </Card>
    ))}
  </div>
);

// ============================================================
// Tab: Keyword Analysis
// ============================================================
const KeywordTab = ({ lang, t, accessCode }) => {
  const [keyword, setKeyword] = useState("");
  const [matchedPosts, setMatchedPosts] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const searchKeyword = async () => {
    if (!keyword.trim()) return;
    setSearching(true);
    setMatchedPosts(null);
    setData(null);
    try {
      const resp = await axios.get(`${API}/api/insights/targeted/search-posts`, {
        params: { q: keyword.trim(), limit: 30 },
        headers: { "x-access-code": accessCode },
      });
      setMatchedPosts(resp.data || []);
    } catch { setMatchedPosts([]); }
    finally { setSearching(false); }
  };

  const analyze = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.post(`${API}/api/insights/targeted/keyword`,
        { keyword: keyword.trim(), language: lang },
        { headers: { "x-access-code": accessCode, "Content-Type": "application/json" }, timeout: 120000 }
      );
      setData(resp.data);
    } catch { setError(t.common.error); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="keyword-tab">
      {/* Search bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchKeyword()}
            placeholder={t.keyword.placeholder}
            className="pl-9 bg-card border-border/60"
            data-testid="keyword-input"
          />
        </div>
        <Button onClick={searchKeyword} disabled={searching || !keyword.trim()} variant="outline" data-testid="keyword-search-btn">
          {searching ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          <span className="ml-1.5 hidden sm:inline">{t.keyword.search}</span>
        </Button>
      </div>

      {/* Matched posts preview */}
      {matchedPosts !== null && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{matchedPosts.length}</span> {t.keyword.found}
            </span>
            {matchedPosts.length > 0 && (
              <Button onClick={analyze} disabled={loading} size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="keyword-analyze-btn">
                {loading ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
                {loading ? t.common.analyzing : t.keyword.analyze}
              </Button>
            )}
          </div>
          {matchedPosts.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-border/40 p-2 bg-card/50">
              {matchedPosts.slice(0, 15).map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded hover:bg-muted/30">
                  <Badge variant="outline" className={`text-[9px] shrink-0 ${getPlatformColor(p.platform)}`}>{p.platform}</Badge>
                  <span className="truncate text-foreground/80">{p.title || "Untitled"}</span>
                  <span className="ml-auto text-muted-foreground shrink-0">{(p.views || 0).toLocaleString()}</span>
                </div>
              ))}
              {matchedPosts.length > 15 && <div className="text-[10px] text-muted-foreground text-center py-1">+{matchedPosts.length - 15} more</div>}
            </div>
          )}
          {matchedPosts.length === 0 && (
            <Card className="border-dashed"><CardContent className="py-6 text-center text-sm text-muted-foreground">{t.keyword.noResult}</CardContent></Card>
          )}
        </div>
      )}

      {error && <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-sm">{error}</div>}
      {loading && <LoadingSkeleton />}
      {!loading && data && <MetaBadges data={data} t={t} />}
      {!loading && data && <AnalysisResult data={data} />}
    </div>
  );
};

// ============================================================
// Tab: Platform Analysis
// ============================================================
const PlatformTab = ({ lang, t, accessCode }) => {
  const [platform, setPlatform] = useState("");
  const [period, setPeriod] = useState("30d");
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch accounts when platform changes
  useEffect(() => {
    if (!platform) { setAccounts([]); setSelectedAccountIds([]); return; }
    axios.get(`${API}/api/insights/targeted/accounts`, {
      params: { platform },
      headers: { "x-access-code": accessCode },
    }).then(r => {
      const accts = r.data || [];
      setAccounts(accts);
      setSelectedAccountIds(accts.map(a => a.id));
    }).catch(() => { setAccounts([]); setSelectedAccountIds([]); });
  }, [platform, accessCode]);

  const toggleAccount = (id) => {
    setSelectedAccountIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => setSelectedAccountIds(accounts.map(a => a.id));

  const formatDate = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const dateLabel = dateRange.from && dateRange.to
    ? `${formatDate(dateRange.from)} ~ ${formatDate(dateRange.to)}`
    : t.platform.custom;

  const analyze = async () => {
    if (!platform) return;
    setLoading(true);
    setError(null);
    try {
      const body = { platform, period, language: lang };
      if (period === "custom" && dateRange.from && dateRange.to) {
        body.start_date = formatDate(dateRange.from);
        body.end_date = formatDate(dateRange.to);
      }
      // Send account_ids only if not all selected
      if (selectedAccountIds.length > 0 && selectedAccountIds.length < accounts.length) {
        body.account_ids = selectedAccountIds;
      }
      const resp = await axios.post(`${API}/api/insights/targeted/platform`,
        body,
        { headers: { "x-access-code": accessCode, "Content-Type": "application/json" }, timeout: 120000 }
      );
      setData(resp.data);
    } catch { setError(t.common.error); }
    finally { setLoading(false); }
  };

  const canAnalyze = platform && selectedAccountIds.length > 0 && (period !== "custom" || (dateRange.from && dateRange.to));

  return (
    <div className="space-y-4" data-testid="platform-tab">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Platform selector */}
        <div className="flex-1 space-y-1.5">
          <label className="text-xs text-muted-foreground font-medium">{t.platform.select}</label>
          <div className="flex flex-wrap gap-1.5">
            {PLATFORMS.map(p => (
              <button
                key={p.value}
                onClick={() => setPlatform(p.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${platform === p.value ? p.color + " ring-1 ring-current" : "border-border/40 text-muted-foreground hover:text-foreground hover:bg-muted/30"}`}
                data-testid={`platform-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Period selector */}
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground font-medium">{t.platform.period}</label>
          <div className="flex gap-1 items-center">
            {PERIODS.map(p => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${period === p.value ? "bg-primary text-primary-foreground border-primary" : "border-border/40 text-muted-foreground hover:text-foreground hover:bg-muted/30"}`}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
            {/* Custom date range */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  onClick={() => setPeriod("custom")}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all flex items-center gap-1 ${period === "custom" ? "bg-primary text-primary-foreground border-primary" : "border-border/40 text-muted-foreground hover:text-foreground hover:bg-muted/30"}`}
                  data-testid="period-custom"
                >
                  <CalendarIcon className="h-3 w-3" />
                  {period === "custom" && dateRange.from ? dateLabel : t.platform.custom}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range || { from: undefined, to: undefined });
                    if (range?.from && range?.to) {
                      setPeriod("custom");
                      setCalendarOpen(false);
                    }
                  }}
                  numberOfMonths={2}
                  disabled={{ after: new Date() }}
                  data-testid="date-range-calendar"
                />
              </PopoverContent>
            </Popover>
          </div>
          {period === "custom" && dateRange.from && dateRange.to && (
            <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
              <CalendarIcon className="h-3 w-3" />
              {dateLabel}
            </div>
          )}
        </div>
      </div>

      {/* Account selector - shown when platform has multiple accounts */}
      {platform && accounts.length > 1 && (
        <div className="space-y-1.5" data-testid="account-selector">
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground font-medium flex items-center gap-1">
              <Users className="h-3 w-3" />
              {lang === "ko" ? "계정 선택" : lang === "zh" ? "选择账号" : "Select Accounts"}
            </label>
            <button
              onClick={selectAll}
              className={`text-[10px] px-2 py-0.5 rounded border transition-all ${selectedAccountIds.length === accounts.length ? "bg-primary/10 text-primary border-primary/30" : "border-border/40 text-muted-foreground hover:text-foreground"}`}
              data-testid="account-select-all"
            >
              {lang === "ko" ? "전체" : lang === "zh" ? "全部" : "All"}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {accounts.map(a => {
              const isSelected = selectedAccountIds.includes(a.id);
              return (
                <button
                  key={a.id}
                  onClick={() => toggleAccount(a.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${isSelected ? getPlatformColor(platform) + " ring-1 ring-current" : "border-border/40 text-muted-foreground hover:text-foreground hover:bg-muted/30"}`}
                  data-testid={`account-${a.id}`}
                >
                  <div className={`h-3 w-3 rounded-sm border flex items-center justify-center ${isSelected ? "bg-current border-current" : "border-muted-foreground/40"}`}>
                    {isSelected && <CheckCircle2 className="h-2.5 w-2.5 text-background" />}
                  </div>
                  {a.display_name || a.username || "Unknown"}
                  {a.followers > 0 && <span className="text-[10px] opacity-60">({(a.followers || 0).toLocaleString()})</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Analyze button */}
      <Button onClick={analyze} disabled={loading || !canAnalyze} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="platform-analyze-btn">
        {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
        {loading ? t.common.analyzing : t.platform.analyze}
      </Button>

      {/* Meta summary */}
      {!loading && data?.meta && (
        <div className="flex flex-wrap gap-3 text-xs">
          <Badge variant="outline" className={getPlatformColor(data.meta.platform)}>{data.meta.platform}</Badge>
          <span className="text-muted-foreground flex items-center gap-1"><CalendarIcon className="h-3 w-3" />{data.meta.start_date} ~ {data.meta.end_date}</span>
          <span className="text-muted-foreground flex items-center gap-1"><Layers className="h-3 w-3" />{data.meta.total_posts} posts</span>
          <span className="text-muted-foreground flex items-center gap-1"><Eye className="h-3 w-3" />{(data.meta.total_views || 0).toLocaleString()} {t.common.views}</span>
          <span className="text-muted-foreground flex items-center gap-1"><Heart className="h-3 w-3" />{(data.meta.total_likes || 0).toLocaleString()} {t.common.likes}</span>
          <span className="text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3" />{data.meta.avg_er}% {t.common.er}</span>
        </div>
      )}

      {error && <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-sm">{error}</div>}
      {loading && <LoadingSkeleton />}
      {!loading && data && <MetaBadges data={data} t={t} />}
      {!loading && data && <AnalysisResult data={data} />}
    </div>
  );
};

// ============================================================
// Tab: Post Analysis
// ============================================================
const PostTab = ({ lang, t, accessCode }) => {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  const searchPosts = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    try {
      const resp = await axios.get(`${API}/api/insights/targeted/search-posts`, {
        params: { q: q.trim(), limit: 15 },
        headers: { "x-access-code": accessCode },
      });
      setSearchResults(resp.data || []);
    } catch { setSearchResults([]); }
  }, [accessCode]);

  const handleSearch = (val) => {
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchPosts(val), 300);
  };

  const togglePost = (post) => {
    const exists = selected.find(s => s.post_id === post.post_id);
    if (exists) {
      setSelected(selected.filter(s => s.post_id !== post.post_id));
    } else if (selected.length < 10) {
      setSelected([...selected, post]);
    }
  };

  const analyze = async () => {
    if (!selected.length) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.post(`${API}/api/insights/targeted/posts`,
        { post_ids: selected.map(s => s.post_id), language: lang },
        { headers: { "x-access-code": accessCode, "Content-Type": "application/json" }, timeout: 120000 }
      );
      setData(resp.data);
    } catch { setError(t.common.error); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="post-tab">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t.post.search}
          className="pl-9 bg-card border-border/60"
          data-testid="post-search-input"
        />
      </div>

      {/* Selected posts */}
      {selected.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{selected.length}</span> {t.post.selected} ({t.post.max})
            </span>
            <Button onClick={analyze} disabled={loading} size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="post-analyze-btn">
              {loading ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
              {loading ? t.common.analyzing : t.post.analyze}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selected.map((p, i) => (
              <Badge
                key={i}
                variant="outline"
                className={`text-[10px] cursor-pointer hover:opacity-70 ${getPlatformColor(p.platform)}`}
                onClick={() => togglePost(p)}
                data-testid={`selected-post-${p.post_id}`}
              >
                {p.platform} | {(p.title || "").slice(0, 25)}... x
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Search results */}
      {searchResults.length > 0 && (
        <div className="max-h-56 overflow-y-auto space-y-1 rounded-lg border border-border/40 p-2 bg-card/50">
          {searchResults.map((p, i) => {
            const isSelected = selected.some(s => s.post_id === p.post_id);
            return (
              <button
                key={i}
                onClick={() => togglePost(p)}
                className={`w-full flex items-center gap-2 text-xs py-2 px-2.5 rounded text-left transition-colors ${isSelected ? "bg-violet-500/10 border border-violet-500/20" : "hover:bg-muted/30 border border-transparent"}`}
                data-testid={`search-result-${p.post_id}`}
              >
                <div className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? "bg-violet-500 border-violet-500" : "border-border"}`}>
                  {isSelected && <CheckCircle2 className="h-3 w-3 text-white" />}
                </div>
                <Badge variant="outline" className={`text-[9px] shrink-0 ${getPlatformColor(p.platform)}`}>{p.platform}</Badge>
                <span className="truncate text-foreground/80">{p.title || "Untitled"}</span>
                <span className="ml-auto text-muted-foreground shrink-0 tabular-nums">{(p.views || 0).toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      )}

      {searchResults.length === 0 && query && <div className="text-xs text-muted-foreground text-center py-3">{t.post.noResult}</div>}

      {error && <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-sm">{error}</div>}
      {loading && <LoadingSkeleton />}
      {!loading && data && <MetaBadges data={data} t={t} />}
      {!loading && data && <AnalysisResult data={data} />}
    </div>
  );
};

// ============================================================
// Main Page
// ============================================================
const TABS = [
  { id: "keyword", icon: Search },
  { id: "platform", icon: Globe },
  { id: "posts", icon: Layers },
];

const AIInsightsPage = ({ lang: uiLang = "en" }) => {
  const [activeTab, setActiveTab] = useState("keyword");
  const [analysisLang, setAnalysisLang] = useState("ko");
  const t = UI[uiLang] || UI.en;
  const accessCode = localStorage.getItem("access_code") || sessionStorage.getItem("access_code") || "";

  return (
    <div className="space-y-5" data-testid="ai-insights-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-violet-400" />
            {t.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t.subtitle}</p>
        </div>
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5" data-testid="language-selector">
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              onClick={() => setAnalysisLang(l.code)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${analysisLang === l.code ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`lang-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 bg-muted/30 rounded-lg p-1 border border-border/30" data-testid="tab-nav">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-all ${activeTab === tab.id ? "bg-card text-foreground shadow-sm border border-border/40" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.tabs[tab.id]}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === "keyword" && <KeywordTab lang={analysisLang} t={t} accessCode={accessCode} />}
      {activeTab === "platform" && <PlatformTab lang={analysisLang} t={t} accessCode={accessCode} />}
      {activeTab === "posts" && <PostTab lang={analysisLang} t={t} accessCode={accessCode} />}
    </div>
  );
};

export default AIInsightsPage;
